// Confluence-style wiki: a page tree on the left, page content on the right.
// Pages + the structured API reference come from the API. Most pages are
// markdown; the API Reference renders Swagger-style from normalized endpoint
// data, each deep-linking to the ticket that uses it.
import { useEffect } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiFileText, FiCode, FiLink } from "react-icons/fi";
import { getWiki, getWikiPage } from "../../features/simulation/simulationSlice";
import { useSimulation } from "./SimulationContext";
import { Avatar } from "./mithraUi";
import MithraMarkdown from "./mithraMarkdown";

// A stable, guessable anchor id for a deep-link target. Lowercase, and every run
// of non-alphanumerics collapses to a single hyphen (braces are dropped first so a
// path param reads cleanly). Endpoints key off method + path, group headings off
// their name - e.g. GET /crate-co/orders/{trackingNumber} -> get-crate-co-orders-trackingnumber.
function anchorId(...parts) {
  return parts
    .join(" ")
    .toLowerCase()
    .replace(/[{}]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function Operation({ op, base, id, pagePath }) {
  const verb = (op.method || "").toLowerCase();
  const errors = op.errorCases || [];
  return (
    <div className="mi-api-op" id={id}>
      <div className="mi-api-op-head">
        <Link
          className="mi-anchor"
          to={`${pagePath}#${id}`}
          title="Link to this endpoint"
          aria-label="Link to this endpoint"
        >
          <FiLink />
        </Link>
        <span className={`mi-verb ${verb}`}>{op.method}</span>
        <span className="mi-api-path">{op.path}</span>
        <span className="mi-api-summary">{op.summary}</span>
      </div>
      <div className="mi-api-body">
        <div className="lbl">Request</div>
        <pre>{op.request}</pre>
        <div className="lbl">Response</div>
        <pre>{op.response}</pre>
        {op.notes && (
          <p style={{ fontSize: 12.5, color: "var(--mi-text-subtle)", margin: "2px 0 8px" }}>
            {op.notes}
          </p>
        )}
        {errors.length > 0 && (
          <>
            <div className="lbl">Errors</div>
            <ul style={{ margin: "0 0 8px 18px", fontSize: 12.5, color: "var(--mi-text-subtle)" }}>
              {errors.map((e, i) => (
                <li key={i}>
                  <code>{e}</code>
                </li>
              ))}
            </ul>
          </>
        )}
        {op.usedInCardKey && (
          <div style={{ fontSize: 12.5 }}>
            Used in <Link to={`${base}/ticket/${op.usedInCardKey}`}>{op.usedInCardKey}</Link>
          </div>
        )}
      </div>
    </div>
  );
}

function ApiReference({ meta, groups, base, pagePath }) {
  return (
    <div className="mi-prose">
      <h1>{meta.title}</h1>
      <p style={{ color: "var(--mi-text-subtle)" }}>
        <code>v{meta.version}</code> · Base URL <code>{meta.baseUrl}</code>
      </p>
      <p>{meta.description}</p>
      <blockquote>
        <strong>Auth.</strong> {meta.auth}
      </blockquote>
      {(groups || []).map((g, i) => (
        <div key={i}>
          <h2 id={anchorId(g.name)} className="mi-api-group">
            {g.name}
          </h2>
          {g.endpoints.map((op, j) => (
            <Operation
              key={j}
              op={op}
              base={base}
              pagePath={pagePath}
              id={anchorId(op.method, op.path)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function MithraWiki() {
  const { slug, pageSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { peopleByKey } = useSimulation();
  const { wikiPages, wikiPage } = useSelector((s) => s.simulation);

  const base = `/simulations/${slug}`;
  const activeSlug = pageSlug || "home";
  const pagePath = `${base}/wiki/${activeSlug}`;

  useEffect(() => {
    dispatch(getWiki(slug));
  }, [dispatch, slug]);

  useEffect(() => {
    dispatch(getWikiPage({ slug, pageSlug: activeSlug }));
  }, [dispatch, slug, activeSlug]);

  const active = wikiPage && wikiPage.slug === activeSlug ? wikiPage : null;
  const author = active ? peopleByKey[active.author] : null;

  // Deep links carry the target section in the URL hash (e.g. .../api-reference
  // #get-crate-co-orders-trackingnumber). Once the page has rendered, scroll that
  // section into view and flash it so the reader can see exactly where they landed.
  // Keyed on active.slug (the loaded page) plus hash/key so a fresh tab, a same-page
  // hash change, and re-clicking the same link all re-trigger.
  useEffect(() => {
    if (!active || !location.hash) return;
    const id = decodeURIComponent(location.hash.slice(1));
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.remove("mi-flash");
      void el.offsetWidth; // reflow so the animation restarts if the class is still present
      el.classList.add("mi-flash");
      el.addEventListener("animationend", () => el.classList.remove("mi-flash"), {
        once: true,
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [active && active.slug, location.hash, location.key]);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "230px 1fr", gap: 26, alignItems: "start" }}>
      {/* Page tree */}
      <aside>
        <div className="mi-nav-section" style={{ paddingTop: 0 }}>
          Engineering wiki
        </div>
        {(wikiPages || []).map((p) => {
          const isActive = p.slug === activeSlug;
          return (
            <div
              key={p.slug}
              className={`mi-navitem ${isActive ? "active" : ""}`}
              onClick={() => navigate(`${base}/wiki/${p.slug}`)}
            >
              {p.kind === "api-reference" ? <FiCode /> : <FiFileText />}
              <span>{p.title}</span>
            </div>
          );
        })}
      </aside>

      {/* Page. min-width:0 lets this 1fr grid item shrink below its content's
          intrinsic width, so wide <pre> blocks scroll internally (overflow-x)
          instead of stretching the column and bleeding off the right edge. */}
      <article style={{ minWidth: 0 }}>
        {!active && <div className="mi-empty">Loading page…</div>}
        {active && (
          <>
            <div className="mi-crumbs">
              {active.space} / <span>{active.title}</span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 16,
                paddingBottom: 12,
                borderBottom: "1px solid var(--mi-border)",
              }}
            >
              {author && <Avatar person={author} size="sm" />}
              <span style={{ fontSize: 12.5, color: "var(--mi-text-subtle)" }}>
                Last edited by <strong>{author ? author.name : active.author}</strong>
                {author ? ` · ${author.team}` : ""}
              </span>
            </div>

            {active.kind === "api-reference" ? (
              <ApiReference
                meta={active.apiMeta || {}}
                groups={active.apiGroups}
                base={base}
                pagePath={pagePath}
              />
            ) : (
              <MithraMarkdown>{active.body}</MithraMarkdown>
            )}
          </>
        )}
      </article>
    </div>
  );
}

export default MithraWiki;
